/**
 * Read executive calendar events from the Firestore mirror (fast local read).
 */

import { collection, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import type { DelegateCalendarEventRow } from './executiveCalendarDelegatedService';

export const EXEC_CAL_MIRROR_COLLECTION = 'executiveCalendarEvents';

function mirrorDocToEvent(data: Record<string, unknown>): DelegateCalendarEventRow {
  return {
    id: String(data.eventId || ''),
    calendarId: String(data.calendarId || ''),
    title: String(data.title || '(No title)'),
    description: data.description as string | undefined,
    start: data.start as string | undefined,
    end: data.end as string | undefined,
    location: data.location as string | undefined,
    htmlLink: data.htmlLink as string | undefined,
    isAllDay: !!data.isAllDay,
    extendedPropertiesPrivate: data.extendedPropertiesPrivate as Record<string, string> | undefined,
  };
}

function overlapsWindow(
  startMs: number,
  endMs: number,
  windowStartMs: number,
  windowEndMs: number,
): boolean {
  return endMs >= windowStartMs && startMs <= windowEndMs;
}

/** One-shot read from the Firestore event mirror. */
export async function fetchMirrorEvents(
  executiveUid: string,
  calendarIds: string[],
  windowStartMs: number,
  windowEndMs: number,
): Promise<DelegateCalendarEventRow[]> {
  if (!executiveUid || !calendarIds.length) return [];
  const calSet = new Set(calendarIds);
  const { getDocs } = await import('firebase/firestore');
  const q = query(
    collection(db, EXEC_CAL_MIRROR_COLLECTION),
    where('executiveUid', '==', executiveUid),
    where('startMs', '>=', windowStartMs - 7 * 24 * 60 * 60 * 1000),
    where('startMs', '<=', windowEndMs),
  );
  const snap = await getDocs(q);
  const rows: DelegateCalendarEventRow[] = [];
  snap.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    const calId = String(data.calendarId || '');
    if (!calSet.has(calId)) return;
    const startMs = typeof data.startMs === 'number' ? data.startMs : 0;
    const endMs = typeof data.endMs === 'number' ? data.endMs : startMs;
    if (!overlapsWindow(startMs, endMs, windowStartMs, windowEndMs)) return;
    if (data.status === 'cancelled') return;
    rows.push(mirrorDocToEvent(data));
  });
  rows.sort((a, b) => new Date(a.start || 0).getTime() - new Date(b.start || 0).getTime());
  return rows;
}

/** Live updates when the mirror changes (another tab, background Google sync). */
export function subscribeMirrorEvents(
  executiveUid: string,
  calendarIds: string[],
  windowStartMs: number,
  windowEndMs: number,
  cb: (events: DelegateCalendarEventRow[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  if (!executiveUid || !calendarIds.length) {
    cb([]);
    return () => undefined;
  }
  const calSet = new Set(calendarIds);
  const q = query(
    collection(db, EXEC_CAL_MIRROR_COLLECTION),
    where('executiveUid', '==', executiveUid),
    where('startMs', '>=', windowStartMs - 7 * 24 * 60 * 60 * 1000),
    where('startMs', '<=', windowEndMs),
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows: DelegateCalendarEventRow[] = [];
      snap.forEach((d) => {
        const data = d.data() as Record<string, unknown>;
        const calId = String(data.calendarId || '');
        if (!calSet.has(calId)) return;
        const startMs = typeof data.startMs === 'number' ? data.startMs : 0;
        const endMs = typeof data.endMs === 'number' ? data.endMs : startMs;
        if (!overlapsWindow(startMs, endMs, windowStartMs, windowEndMs)) return;
        if (data.status === 'cancelled') return;
        rows.push(mirrorDocToEvent(data));
      });
      rows.sort((a, b) => new Date(a.start || 0).getTime() - new Date(b.start || 0).getTime());
      cb(rows);
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  );
}
